import React, { useState, useEffect, useCallback } from 'react';
import { documentAPI } from '../../services/api';
import PhotoAnnotator from './PhotoAnnotator';
import './photoLightbox.css';

const PhotoLightbox = ({ photos, initialIndex = 0, onClose, onDelete, projectId, onSaved }) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [annotating, setAnnotating] = useState(false);

    const photo = photos[currentIndex];

    const loadImage = useCallback(async (idx) => {
        if (!photos[idx]) return;
        setLoading(true);
        try {
            const { data } = await documentAPI.getDownloadUrl(photos[idx].id);
            setImageUrl(data.downloadUrl);
        } catch (err) {
            console.error('Failed to load image:', err);
            setImageUrl(null);
        } finally {
            setLoading(false);
        }
    }, [photos]);

    useEffect(() => {
        loadImage(currentIndex);
    }, [currentIndex, loadImage]);

    // Lock body scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && currentIndex > 0) setCurrentIndex(i => i - 1);
            if (e.key === 'ArrowRight' && currentIndex < photos.length - 1) setCurrentIndex(i => i + 1);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [currentIndex, photos.length, onClose]);

    const handleDownload = async () => {
        if (!photo) return;
        try {
            const { data } = await documentAPI.getDownloadUrl(photo.id);
            const response = await fetch(data.downloadUrl);
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = photo.fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Download failed:', err);
        }
    };

    const handleDelete = async () => {
        if (!photo) return;
        if (!window.confirm(`Delete "${photo.fileName}"?`)) return;
        try {
            await documentAPI.delete(photo.id);
            if (onDelete) onDelete();
            // Auto-close if last photo
            if (photos.length <= 1) {
                onClose();
            } else if (currentIndex >= photos.length - 1) {
                setCurrentIndex(i => i - 1);
            }
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    if (!photo) return null;

    return (
        <div className="lightbox-overlay" onClick={onClose}>
            <div className="lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
                <div className="lightbox-info">
                    <span className="lightbox-filename">{photo.fileName}</span>
                    <span className="lightbox-date">
                        {new Date(photo.uploadedAt).toLocaleDateString()}
                    </span>
                    <span className="lightbox-counter">
                        {currentIndex + 1} / {photos.length}
                    </span>
                </div>
                <div className="lightbox-actions">
                    <button className="lightbox-btn" onClick={handleDownload}>
                        <i className="fas fa-download"></i> Download
                    </button>
                    <button className="lightbox-btn" onClick={() => setAnnotating(true)}>
                        <i className="fas fa-pencil-alt"></i> Draw
                    </button>
                    <button className="lightbox-btn delete" onClick={handleDelete}>
                        <i className="fas fa-trash-alt"></i> Delete
                    </button>
                    <button className="lightbox-close" onClick={onClose}>
                        &times;
                    </button>
                </div>
            </div>

            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                {currentIndex > 0 && (
                    <button
                        className="lightbox-arrow left"
                        onClick={() => setCurrentIndex(i => i - 1)}
                    >
                        <i className="fas fa-chevron-left"></i>
                    </button>
                )}

                {loading ? (
                    <div className="lightbox-loading">Loading...</div>
                ) : imageUrl ? (
                    <img
                        src={imageUrl}
                        alt={photo.fileName}
                        className="lightbox-image"
                        draggable={false}
                    />
                ) : (
                    <div className="lightbox-loading">Failed to load image</div>
                )}

                {currentIndex < photos.length - 1 && (
                    <button
                        className="lightbox-arrow right"
                        onClick={() => setCurrentIndex(i => i + 1)}
                    >
                        <i className="fas fa-chevron-right"></i>
                    </button>
                )}
            </div>

            {annotating && (
                <PhotoAnnotator
                    photo={photo}
                    imageUrl={imageUrl}
                    projectId={projectId}
                    onClose={() => setAnnotating(false)}
                    onSaved={() => { setAnnotating(false); if (onSaved) onSaved(); }}
                />
            )}
        </div>
    );
};

export default PhotoLightbox;
